import { NextCustomImage as NextImage } from 'components/Image';
import Link from 'components/Globals/Base/Link';
import Heading from 'styles/typography/Heading';
import type { FontFamily, FontSizes, ResponsiveStyling } from 'styles/theme/types';
import type { MediaItem } from 'interfaces/content/Media';
import { ParseHtmlDefault } from 'utils/miscUtils/rawHtml';
import * as S from './RankingCardStyles';

export interface RankingCardProps {
  title: string;
  url: string;
  variant: 'card-hero' | 'card-row' | 'card-column' | 'card-noimage' | 'card-link';
  image?: MediaItem;
  description?: string;
  cta?: {
    title: string;
    url: string;
  };
}

function getHeadingProps(rankingCardVariant: RankingCardProps['variant']): {
  $size: ResponsiveStyling<keyof Omit<FontSizes, 'xlarge' | 'xxlarge'>>;
  $fontFamily: FontFamily;
} {
  switch (rankingCardVariant) {
    case 'card-hero':
      return {
        $fontFamily: 'saol',
        $size: { default: 'level4', mdMin: 'level3' },
      };
    case 'card-row':
      return {
        $fontFamily: 'graphikCond',
        $size: { default: 'level4' },
      };
    case 'card-column':
      return {
        $fontFamily: 'saol',
        $size: { default: 'level6', xlMin: 'level4' },
      };
    case 'card-noimage':
      return {
        $fontFamily: 'saol',
        $size: { default: 'level4', mdMin: 'level3', xlMin: 'level2' },
      };
    case 'card-link':
      return {
        $fontFamily: 'graphikCond',
        $size: { default: 'level6' },
      };
    default:
      return {
        $fontFamily: 'saol',
        $size: { default: 'level4' },
      };
  }
}

const RankingCard = ({ description, title, image, url, variant, cta }: RankingCardProps) => (
  <S.Wrapper $variant={variant}>
    {variant !== 'card-noimage' && variant !== 'card-link' && (
      <Link
        href={url}
        type='image'
        className='item-image'
      >
        {image?.src && (
          <NextImage
            width={570}
            height={379}
            object-fit='cover'
            src={image.src}
            alt={image.altText || title}
            placeholder='blur'
          />
        )}
      </Link>
    )}
    <Link
      href={url}
      type='brandPrimary'
      className='item-title'
    >
      <Heading {...getHeadingProps(variant)}>{title}</Heading>
    </Link>
    {variant !== 'card-link' && (
      <>
        {description && <div className='item-description'>{ParseHtmlDefault(description)}</div>}
        {cta?.url && (
          <Link
            className='cta'
            href={cta.url}
            type='primaryBrandButton'
          >
            {cta.title}
          </Link>
        )}
      </>
    )}
  </S.Wrapper>
);

export default RankingCard;
